<template>
  <div v-if="connection.label !== null" @keydown.tab.prevent="onTab">
    <rich-text-editor
      ref="labelEditor"
      class="labelEditor"
      :content="connection.label"
      :editable="canEdit"
      :activate-on-double-click="false"
      :extension-options="extensionOptions"
      @blur="onBlurLabel"
      @focus="onFocusLabel"
      @update="onUpdateLabel"
    />
    <div
      v-if="canEdit"
      class="remove"
      :class="{ visible: closeVisible }"
      @mousedown.left="onRemove"
    >
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 213 213"
        xml:space="preserve"
      >
        <path
          d="M131.8,106.5l75.9-75.9c7-7,7-18.3,0-25.3c-7-7-18.3-7-25.3,0l-75.9,75.9L30.6,5.2c-7-7-18.3-7-25.3,0c-7,7-7,18.3,0,25.3l75.9,75.9L5.2,182.4c-7,7-7,18.3,0,25.3c7,7,18.3,7,25.3,0l75.9-75.9l75.9,75.9c7,7,18.3,7,25.3,0c7-7,7-18.3,0-25.3L131.8,106.5z"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import emitter from 'tiny-emitter/instance';
import { EVENT_CONNECTION_CLICK } from '@jsplumb/browser-ui';
import { DotEndpoint } from '@jsplumb/core';
import jspSingleton from '@/lib/jspSingleton.js';
import uuidv4 from 'uuid/v4';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, JSPLUMB } from '@/constants.js';
import misc from '../mixins/misc.js';
import Point from '../../point.js';
import Rect from '../../rect.js';
import RichTextEditor from '../all/RichText/RichTextEditor.vue';

export default {
  components: {
    'rich-text-editor': RichTextEditor,
  },

  mixins: [misc],
  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
    defaultColor: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      extensionOptions: {
        hardbreak: true,
        bold: true,
        italic: true,
        underline: true,
        horizontalRule: false,
        customLink: true,
        history: true,
        placeholder: {
          text: 'Type label...',
        },
        dropEventHandler: true,
        codeBlockHighlight: { toolbarButton: true },
        customMention: true,
      },
      label: null,
      showSelectionsTimer: null,
      closeVisible: false,
    };
  },

  computed: {
    ...mapGetters('project/config/connections', ['getIsSelected']),
    ...mapGetters('project/data/connections', ['getNode']),
    ...mapGetters('project/data/conditions', { getCondition: 'getNode' }),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('project/config/elements', {
      getElementSelection: 'getSelections',
    }),
    ...mapGetters('project/config/notes', {
      getNoteSelection: 'getSelections',
    }),
    ...mapGetters('project/config/connections', {
      getConnectionSelection: 'getSelections',
    }),
    ...mapGetters('project/config/jumpers', {
      getJumperSelection: 'getSelections',
    }),
    ...mapGetters('application/appStates', ['getShowSelections']),

    /**
     * Returns the current connection object
     * @returns {Object} The current connection
     */
    connection() {
      return this.getNode(this.id);
    },

    /**
     * Returns whether this connection is selected
     * @returns {Boolean} Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    /**
     * Return the collaboarator that has selected this
     */
    selectedByCollaborator() {
      const collaborators = this.getCollaborators.filter(
        c =>
          c.selections &&
          c.selections.connections &&
          c.selections.connections.includes(this.id)
      );
      if (collaborators.length > 0) return collaborators[0];
      return null;
    },

    /**
     * Returns the connection label id
     * @returns {String}  The label id
     */
    labelId() {
      return `label_${this.connection.id}`;
    },
  },

  watch: {
    /**
     * A connections selection state has changed, update its style.
     */
    isSelected(newValue, oldValue) {
      console.log('****CONNECTION WATCHER - ISSELECTED');
      this.updateStyle();
    },

    /**
     * A connection has been selected by another collaborator, update its style.
     */
    selectedByCollaborator(newValue, oldValue) {
      console.log('****CONNECTION WATCHER - SELECTEDBYCOLLABORATOR');

      this.updateStyle();
    },

    /**
     * A connections virtual selection state has chenged, update its style.
     */
    virtualSelected(newValue, oldValue) {
      console.log('****CONNECTION WATCHER - VIRTUALSELECTED');

      this.updateStyle();
    },

    /**
     * The show selections has changed, repaint the connection.
     */
    getShowSelections(newValue, oldValue) {
      this.updateStyle();
    },

    /**
     * A connections style type has changed, update jsplumb.
     */
    'connection.type': function type(newValue, oldValue) {
      if (!newValue) return;
      if (this.jspConnection) {
        jspSingleton.instance
          .select({ connections: [this.jspConnection] })
          .setConnector(newValue);
        this.jspConnection.addOverlay(JSPLUMB.arrowOverlay);
        this.updateStyle();
        // Until addOverlay uses revalidate by itself
        jspSingleton.instance.revalidate(this.jspConnection.source);
      }
    },

    /**
     * A connection theme has chenged, update its style.
     */
    'connection.theme': function theme(newValue, oldValue) {
      this.updateStyle();
    },

    /**
     * A connection label has changed
     */
    'connection.label': function label(newValue, oldValue) {
      if (newValue !== null && oldValue === null) {
        // Wait for this.el to render (v-if on connection.label)
        this.$nextTick(() => {
          this.createOverlay();
        });
      }

      if (newValue === null) {
        this.removeOverlay(true);
      }
    },
  },

  beforeCreate() {
    // Add in before create and not in data ctx so it is not reactive (and not become a proxy)
    this.jspConnection = null;
  },

  /**
   * mounted lifecycle hook
   */
  mounted() {
    this.setupConnection();

    // Add custom event listeners
    emitter.on(EVENT.CLICK_ENDPOINT, this.onClickEndpoint);
    emitter.on(EVENT.CLICK_CONNECTION, this.onClick);
    emitter.on(EVENT.RIGHT_CLICK_CONNECTION, this.onRightClick);
    emitter.on(EVENT.DBL_CLICK_CONNECTION, this.onDblClick);
    emitter.on(EVENT.DRAG_CONNECTION, this.onDrag);
  },

  beforeUnmount() {
    console.log('Connection::beforeUnmount', this.jspConnection);

    if (this.jspConnection) {
      const { sourceId = null, targetId = null } = this.jspConnection;
      // If connection exists, delete and repaint source-target
      if (this.jspConnection?.endpoints) {
        this.jspConnection.removeClass('selected', true);
        JSPLUMB.removeClass(this.jspConnection, 'theme-', true);
        JSPLUMB.removeClass(this.jspConnection, 'collab-', true);

        // Clean up any overlays
        this.removeOverlay(true);

        // Delete the actual connection
        jspSingleton.instance.deleteConnection(this.jspConnection);
      }

      if (sourceId) {
        const el = document.getElementById(sourceId);
        jspSingleton.instance.revalidate(el);
      }

      if (targetId) {
        const el = document.getElementById(targetId);
        jspSingleton.instance.revalidate(el);
      }
    }

    // Remove event listeners on component unmount
    emitter.off(EVENT.CLICK_CONNECTION, this.onClick);
    emitter.off(EVENT.RIGHT_CLICK_CONNECTION, this.onRightClick);
    emitter.off(EVENT.DBL_CLICK_CONNECTION, this.onDblClick);
    emitter.off(EVENT.DRAG_CONNECTION, this.onDrag);
  },

  methods: {
    ...mapActions('application/contextToolbar', ['setAnchor']),
    ...mapActions('project/data/connections', ['customUpdate', 'customDelete']),
    ...mapActions('project/config/connections', {
      selectConnection: 'select',
      deselectConnection: 'deselect',
    }),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapMutations('application/appStates', ['setShowSelections']),

    /**
     * Set up the jsplumb connection if it does not already exist.
     */
    setupConnection() {
      // Check if the connection already exists in jsplumb
      const {
        sourceType,
        sourceid,
        targetType,
        targetid,
        type,
        theme,
        targetFace,
        sourceFace,
      } = this.connection;
      jspSingleton.instance
        .select({ source: sourceid, target: targetid })
        .each(jspConnection => {
          if (jspConnection.id === this.id) {
            this.jspConnection = jspConnection; // keep a reference to it
          }
        });

      // Create this connection to jsplumb if it does not exist
      if (this.jspConnection === null) {
        // Determine the source DOM element
        let source = null;
        if (sourceType === 'elements' || sourceType === 'branches') {
          source = document.getElementById(sourceid);
        } else if (sourceType === 'conditions') {
          source = document.getElementById(sourceid);
          const endpoints = jspSingleton.instance.getEndpoints(source);
          [source] = endpoints;
        }
        // Determine the target DOM element
        const target = document.getElementById(targetid);

        // Determine the source anchor
        let sourceAnchor = null;
        if (sourceFace) {
          // We anchor to a specific face on anything - use anchor constricted to this face only
          sourceAnchor = JSPLUMB.getFaceSelectorOptions(sourceFace).anchor;
        } else if (sourceType === 'elements') {
          sourceAnchor = JSPLUMB.defaultAnchor;
        } else if (sourceType === 'conditions') {
          sourceAnchor = JSPLUMB.sourceConditionAnchor;
        }

        // Determine the target anchor
        let targetAnchor = null;
        if (targetFace) {
          // In case we anchor to a specific face on anything - use anchor constricted to this face only
          targetAnchor = JSPLUMB.getFaceSelectorOptions(targetFace).anchor;
        } else if (targetType === 'elements' || targetType === 'jumpers') {
          // Elements and jumpers use the default anchor (on all four faces)
          targetAnchor = JSPLUMB.defaultAnchor;
        } else if (targetType === 'branches') {
          // Jumpers use anchor on three faces
          targetAnchor = JSPLUMB.targetBranchAnchor;
        }

        // Create the jsplumb connection
        this.jspConnection = jspSingleton.instance.connect(
          JSPLUMB.connect(
            source,
            target,
            type,
            theme,
            sourceAnchor,
            targetAnchor
          )
        );
      }
      // Create the label overlay
      if (this.connection.label !== null) {
        this.createOverlay();
      }

      this.jspConnection.endpoints.forEach(e => {
        const { classes } = e.endpoint;
        if (
          classes.includes('source') ||
          classes.includes('condition-source')
        ) {
          e.addClass('d-source');
        } else if (classes.includes('target')) {
          e.addClass('d-target');
        }
      });

      // Set the jsplumb connection id to the stored one
      this.jspConnection.id = this.connection.id;
      this.updateStyle();
    },

    /**
     * Event listener - RichTextEditor focus event. Position the toolbar
     */
    onFocusLabel() {
      this.closeVisible = true;
      this.setAnchor({
        anchor: () => {
          const labelRect = this.$el.getBoundingClientRect();
          const rect = new Rect(
            labelRect.left,
            labelRect.right,
            labelRect.top,
            labelRect.bottom
          );
          const parentRect = this.$parent.$el.getBoundingClientRect();
          const parentPt = new Point(parentRect.left, parentRect.top);
          rect.offsetRect(parentPt);
          return rect;
        },
      });
    },

    /**
     * Event listener - RichTextEditor focus out event. Store the updated label.
     */
    onBlurLabel() {
      this.closeVisible = false;

      const { isContentEmpty, getContent } = this.$refs.labelEditor;
      console.log('onBlurLabel isContentEmpty returns:', isContentEmpty());
      const label = isContentEmpty() ? null : getContent();
      if (label !== this.connection.label) {
        this.customUpdate({
          id: this.id,
          props: { label },
          apply: true,
        });
      }
    },

    onTab() {
      this.$refs.labelEditor.blur();
      this.onBlurLabel();
    },

    /**
     * Event listener - the RichTextEditor text is updated
     */
    onUpdateLabel(content) {
      // this.customUpdate({
      //   id: this.id,
      //   props: {
      //     label: content,
      //   },
      //   apply: true,
      // });
    },

    /**
     * Set the paint style (jsplumb) according to connection selected state.
     */
    updateStyle() {
      if (!this.connection) return;
      const { theme = 'default', type } = this.connection;
      // Add a class to connector, endpoints and overlay for the theme
      JSPLUMB.removeClass(this.jspConnection, 'theme-', true);
      this.jspConnection.addClass(`theme-${theme}`, true);

      // Add a class to artifacts when the connection is selected (or virtually selected)
      if ((this.isSelected || this.virtualSelected) && this.getShowSelections) {
        this.jspConnection.addClass('selected', true);
      } else {
        this.jspConnection.removeClass('selected', true);
      }

      // Add a class to artifacts when the connection is selected by a collaborator
      if (this.selectedByCollaborator) {
        const { color } = this.selectedByCollaborator;
        this.addClass(`collab-${color}`, true);
      } else {
        JSPLUMB.removeClass(this.jspConnection, 'collab-', true);
      }
    },

    /**
     * Endpoint click listener for custom event.
     * @param {number} endpoint       The endpoint of the clicked endpoint
     * @param {Event} event           The original event
     */
    onClickEndpoint(endpoint, event) {
      // In case another connection needs to catch the event discard it
      if (endpoint.connections[0].id !== this.connection.id) {
        return;
      }

      // When ctrl or shift pressed toggle selection
      if (event.ctrlKey || event.shiftKey || event.metaKey) {
        if (!this.isSelected) {
          this.selectConnection({ id: this.id, apply: true });
        } else {
          this.deselectConnection({ id: this.id, apply: true });
        }
      } else if (!this.isSelected) {
        // Clear any selections and select only this connection
        this.deselectAll({ apply: true });
        jspSingleton.instance.clearDragSelection();
        this.selectConnection({ id: this.id, apply: true });
      }
    },

    /**
     * Connection click listener for custom event.
     * @param {number} connectionId   The connection id of the clicked label
     * @param {Event} event           The original event
     */
    onClick(connectionId, event) {
      console.log('Connection click', connectionId);
      // In case we click on a label discard the event (do not select)
      if (event.target.closest('.jtk-overlay')) {
        return;
      }
      // In case another connection needs to catch the event discard it
      if (connectionId !== this.id) {
        return;
      }

      // When ctrl or shift pressed toggle selection
      if (event.ctrlKey || event.shiftKey || event.metaKey) {
        if (!this.isSelected) {
          this.selectConnection({ id: this.id, apply: true });
        } else {
          this.deselectConnection({ id: this.id, apply: true });
        }
      } else if (!this.isSelected) {
        // Clear any selections and select only this connection
        this.deselectAll({ apply: true });
        jspSingleton.instance.clearDragSelection();
        this.selectConnection({ id: this.id, apply: true });
      }
    },

    /**
     * Connection right click listener for custom event.
     * @param {number} connectionId   The connection id of the clicked label
     * @param {Event} event           The original event
     */
    onRightClick(connectionId, event) {
      // In case another connection needs to catch the event discard it
      if (connectionId !== this.id) {
        return;
      }
      // Select the connection
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance.clearDragSelection();
        this.selectConnection({ id: this.id, apply: true });
      }
      // Show the context menu
      this.createContextMenu(event.clientX, event.clientY);
    },

    /**
     * Create a context menu for this connection
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      // Check to see if we have any other selections in order to show label related options
      const singleSelection =
        this.getElementSelection.length === 0 &&
        this.getNoteSelection.length === 0 &&
        this.getJumperSelection.length === 0 &&
        this.getConnectionSelection.length === 1;
      if (singleSelection) {
        this.createItem({
          name: this.connection.label === null ? 'Create label' : 'Edit label',
          icon: null,
          commands: {
            doAction: () => {
              this.editLabel();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
        if (this.connection.label !== null) {
          this.createItem({
            name: 'Remove label',
            icon: null,
            commands: {
              doAction: () => {
                this.onRemove();
                this.deleteMenu();
              },
              isActive: () => {
                return true;
              },
              isEnabled: () => {
                return true;
              },
            },
            submenu: null,
          });
        }
      }

      this.createItem({
        name: 'Color',
        showName: false,
        icon: null,
        commands: {
          doAction: () => {},
          closeAction: () => {
            clearTimeout(this.showSelectionsTimer);
            this.setShowSelections({ value: true });
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        addon: {
          component: 'color-picker',
          props: {
            selectedColor: this.connection.theme,
            onChangeColor: () => {
              this.setShowSelections({ value: false });
              clearTimeout(this.showSelectionsTimer);
              this.showSelectionsTimer = setTimeout(
                () => this.setShowSelections({ value: true }),
                1600
              );
            },
          },
        },
      });

      // Check if only connections are selected
      const onlyConnectionsSelection =
        this.getElementSelection.length === 0 &&
        this.getNoteSelection.length === 0 &&
        this.getJumperSelection.length === 0 &&
        this.getConnectionSelection.length > 0;
      if (onlyConnectionsSelection) {
        console.log('Board connection creating menu, this.id:', this.id);
        this.createItem({
          name: 'Style',
          showName: false,
          icon: null,
          commands: {
            doAction: () => {},
            closeAction: () => {},
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          addon: {
            component: 'connection-style-picker',
          },
        });
      }

      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          clearTimeout(this.showSelectionsTimer);
          this.setShowSelections({ value: true });
        },
      });
    },

    /**
     * Event listener - custom double click on connection event
     */
    onDblClick(connectionId, event) {
      // Prevent if user has shift,ctrl or meta pressed
      if (event.ctrlKey || event.shiftKey || event.metaKey) return;
      // In case another connection needs to catch the event discard it
      if (connectionId !== this.id) return;
      this.editLabel();
    },

    /**
     * Event listener - custom drag event (an existing connection has started dragging)
     */
    onDrag(connectionId) {
      // In case another connection needs to catch the event discard it
      if (connectionId !== this.id) return;

      const { labelEditor } = this.$refs;
      // Stop editing the label if there is such a case
      if (labelEditor) {
        // const content = labelEditor.getContent();
        // this.customUpdate({
        //   id: this.id,
        //   props: {
        //     label: content,
        //   },
        //   apply: true,
        // });
        labelEditor.blur();
      }
      console.log('onDrag updating style');
      this.$nextTick(() => {
        this.updateStyle();
      });
    },

    /**
     * Event listener - the user presses the x button to remove the label
     */
    onRemove(event) {
      this.$refs.labelEditor.blur();
      // Wait for the component to re-render
      this.$nextTick(() => {
        this.customUpdate({
          id: this.id,
          props: {
            label: null,
          },
          apply: true,
        });
      });
    },

    /**
     * Create the label overlay for this connection
     */
    createOverlay() {
      const overlay = this.jspConnection.getOverlay('Custom');
      if (!overlay) {
        this.jspConnection.addOverlay({
          type: 'Custom',
          options: {
            create: c => {
              return this.$el;
            },
            id: 'Custom',
            location: 0.5,
            cssClass: 'label',
          },
        });
        // Until addOverlay uses revalidate by itself
        jspSingleton.instance.revalidate(this.jspConnection.source);
      }
    },

    /**
     * Remove the label overlay for this connection
     * @param {boolean} dontCleanup Whether to delete the DOM element for the overlay
     */
    removeOverlay(dontCleanup = false) {
      const overlay = this.jspConnection.getOverlay('Custom');
      if (overlay) {
        this.jspConnection.removeOverlay('Custom', dontCleanup);
      }
    },

    /**
     * Start the label editing
     */
    editLabel() {
      // Update a null label to empty to be able to see it and edit it
      if (!this.connection.label) {
        this.customUpdate({
          id: this.id,
          props: {
            label: '',
          },
          apply: true,
        });
      }
      // Enable richTexteditor
      this.$nextTick(() => {
        this.createOverlay();
        this.$refs.labelEditor.startEditing();
      });
    },
  },
};
</script>

<style lang="scss">
// Theme colors
$theme-colors: (
  'default': #f3f3f3,
  'orange': #c64900,
  'brown': #764700,
  'gold': #cbce00,
  'moss': #7bc800,
  'green': #009d05,
  'cyan': #00ab8c,
  'lightBlue': #009fdd,
  'blue': #005aff,
  'purple': #cc00ff,
  'pink': #c60089,
  'red': #bc1c1c,
);

@each $name, $hex in $theme-colors {
  //iterate and create connection theme styles
  .jtk-connector.theme-#{$name}
    path:not(.jtk-overlay):not(.jtk-connector-outline) {
    stroke: $hex;
  }
  .jtk-connector.theme-#{$name} path[jtk-overlay-id='arrow'] {
    stroke: $hex;
    fill: $hex;
  }

  // Unconnected endpoints do not show up
  .jtk-endpoint.source {
    visibility: hidden;
  }
  .jtk-endpoint.jtk-endpoint-connected.source {
    visibility: visible;
  }
  // Floating target endpoints do not show up (we instead see the arrow overlay)
  .jtk-endpoint.jtk-floating-endpoint {
    visibility: hidden;
  }
  // Unless they are source endpoints
  .jtk-endpoint.jtk-floating-endpoint.d-source {
    visibility: visible;
  }
  // Dragged endpoint (new connection) does not show up
  .source.jtk-endpoint.jtk-dragging {
    visibility: hidden !important;
  }

  svg.jtk-endpoint.jtk-floating-endpoint.theme-#{$name} circle,
  svg.jtk-endpoint.jtk-endpoint-connected.theme-#{$name} circle,
  svg.jtk-endpoint.jtk-endpoint-connected.source.theme-#{$name} circle,
  svg.jtk-endpoint.jtk-endpoint-connected.jtk-endpoint-anchor-theme-#{$name}
    circle,
  svg.jtk-endpoint.jtk-endpoint-connected.condition-source.theme-#{$name}
    circle {
    fill: $hex;
    stroke: transparent;
    stroke-width: 10;
  }
}

// Collaborator colors
$collab-colors: (
  'light-brown': #5e5e5e,
  'gray-blue': #4b667e,
  'light-blue': #265874,
  'blue': #1a4d7d,
  'dark-blue': #114246,
  'light-purple': #5f62ae,
  'dark-purple': #5d337e,
  'light-turqoize': #3d7d79,
  'light-green': #456c4b,
  'dark-green': #3d501d,
  'light-yellow': #8c7946,
  'yellow': #646934,
  'brown': #64501e,
  'orange': #7c513f,
  'pink': #804b71,
  'fire-red': #783232,
  'dark-red': #62312a,
);

@each $name, $hex in $collab-colors {
  //iterate and create colaborator selection styles
  .jtk-connector.collab-#{unquote('\\' + $hex)}
    path:not(.jtk-overlay):not(.jtk-connector-outline) {
    stroke: $hex;
  }
  .jtk-connector.collab-#{unquote('\\' + $hex)} path[jtk-overlay-id='arrow'] {
    stroke: $hex;
    fill: $hex;
  }
  svg.jtk-endpoint.jtk-floating-endpoint.collab-#{unquote('\\' + $hex)} circle,
  svg.jtk-endpoint.jtk-endpoint-connected.source.collab-#{unquote('\\' + $hex)}
    circle,
  svg.jtk-endpoint.jtk-endpoint-connected.condition-source.collab-#{unquote('\\' + $hex)}
    circle {
    fill: $hex;
    stroke: transparent;
    stroke-width: 10;
  }
}

//Selection style
$selected: #db841e;
.jtk-connector.selected path:not(.jtk-overlay):not(.jtk-connector-outline) {
  stroke: $selected;
}

.jtk-connector.selected path[jtk-overlay-id='arrow'] {
  stroke: $selected;
  fill: $selected;
}
svg.jtk-endpoint.jtk-floating-endpoint.selected circle,
svg.jtk-endpoint.jtk-endpoint-connected.selected circle,
svg.jtk-endpoint.jtk-endpoint-connected.source.selected circle,
svg.jtk-endpoint.jtk-endpoint-connected.condition-source.selected circle {
  fill: $selected;
  stroke: transparent;
  stroke-width: 10;
}

//Empty endpoint styles
svg.jtk-endpoint.condition-source circle {
  fill: black;
  stroke: #f3f3f3;
  stroke-width: 2;
}

//Dragging endpoints
svg.jtk-endpoint.jtk-dragging circle {
  stroke-width: 2 !important;
}
// Target endpoints
.jtk-endpoint.target {
  opacity: 0; // They are invisible - only the arrow overlay is shown
}
</style>
